/* Вид формы шаблона «Фото» и «Видео» ↔ входы сервера (план вехи 3, шаг 2 «photo: adapter form»; план фото/видео §2.4). Чистые функции
   без DOM и запросов: сервер экрана (photo-server.ts) снимает вид с состояния движка (S, src(), arOf(), durOf()…), а эти функции
   переводят его в входы опубликованной формы режима и обратно (повтор «Истории», «Изменить», заявка родной формы).
   Правила (как родные PhotoOrderForm и VideoOrderForm):
   - пишутся только опубликованные поля и только опубликованные значения (иначе — значение формы по умолчанию или ключа нет);
   - формат «Как у фото» — auto у фото, src у видео; у «По описанию» — формат вида по умолчанию (arOf шаблона);
   - стиль: свой референс заменяет стиль — style none (MessagingPhotoPolicy: стиль и референс вместе нельзя);
   - кадр crop_x/crop_y/zoom — только когда формат режет снимок (PhotoCrop: не auto и не src), фрагмент start_ms/end_ms — только
     у ролика длиннее 10 секунд (VideoSource: длинный ролик грузится целиком, окно — ключами);
   - длительность ролика по описанию или из фото — ступенями 5 и 10 секунд (цена шаблона: до 5 — как за 5, длиннее — как за 10);
   - «Видео товара»: главное фото — первое в лотке (main_photo 1), движение — выбранная карточка ленты (motion);
   - люди в кадре — строки состава self | user_avatar:<id> | person:<id> | character:<id> в cast_N, действие — action_N;
   - ключи загрузок (photo, video, photo_2…photo_5, reference) пишут мосты загрузок, а не эта функция: они переносятся как есть.
   Режим сервера по виду шаблона (route): у фото — «По описанию» generate, «Из фото» без стиля edit, со стилем или своим референсом style,
   пресеты avatar | restore | colorize; у видео — «По описанию» text, «Из файла» без стиля photo (снимок) или edit (ролик), со стилем style,
   пресеты product («Видео товара») и parallax; шаблон каталога — свой режим (null: без перехода). */
import { studioFields, type StudioForm } from '../studio/studio-api';
import { photoPolicy } from './photo-model';
import { videoPolicy } from '../video/video-model';

export type PhotoKind = 'photo' | 'video';
type Inputs = Record<string, string>;
/** Режим сервера: фото — photo-model PhotoMode, видео — video-model VideoMode */
export type ServerMode = 'generate' | 'style' | 'edit' | 'avatar' | 'restore' | 'colorize' | 'text' | 'photo' | 'product' | 'parallax';

/** Вид формы шаблона, из которого строятся входы */
export interface PhotoView {
  /** S.mode: ph | tx у фото, fl | tx у видео */
  mode: string;
  /** src(): что сейчас исходник — ph (фото или пустая рамка фото), vd (ролик), tx (описание) */
  src: 'ph' | 'vd' | 'tx';
  /** S.preset (ключ PRESETS вида) */
  preset: string | null;
  /** Шаблон каталога (S.app): у него свой режим */
  template: boolean;
  /** String(S.sty): id стиля, 'none' или id движения «Видео товара» */
  sty: string;
  /** noStyle(): «Без стиля» */
  none: boolean;
  /** refAct(): выбран свой референс ленты (заменяет стиль) */
  refAct: boolean;
  text: string;
  /** arOf(): пропорции результата или null («Как у фото» / «Как у видео») */
  ar: string | null;
  /** S.n — сколько фото */
  n: number;
  /** durOf() — длительность ролика, с (0 — длина ролика ещё неизвестна) */
  dur: number;
  /** Кадр фото (S.file.crop) */
  crop: { cx: number; cy: number; z: number } | null;
  /** Окно длинного ролика, с (BC.trim.of) */
  trim: { from: number; to: number } | null;
}
/** Что вход заказа знает о виде (повтор, «Изменить», чужая заявка): только то, что пришло во входах */
export interface PhotoViewPatch {
  sty?: string;
  text?: string;
  /** S.ar: 'src' («Как у фото») или пропорции */
  ar?: string;
  n?: number;
  dur?: number;
  crop?: { cx: number; cy: number; z: number };
  trim?: { from: number; to: number };
  /** Люди в кадре по порядку: строка состава и действие */
  cast?: Array<{ ref: string; action: string }>;
}
/** Части входов, которые собирает сервер экрана, а не вид: люди (по порядку) и блок «Съёмка» (JSON или нет блока) */
export interface PhotoExtras {
  cast?: Array<{ ref: string; action: string }> | null;
  shoot?: string;
}

/** Ключи загрузок экрана: их пишут мосты (media-bridge, ref-bridge), здесь они переносятся как есть */
export const UPLOAD_KEYS = ['photo', 'video', 'photo_2', 'photo_3', 'photo_4', 'photo_5', 'reference'] as const;
/** Слоты лотка «Видео товара» по порядку */
export const TRAY_KEYS = ['photo', 'photo_2', 'photo_3', 'photo_4', 'photo_5'] as const;
const CROP = ['crop_x', 'crop_y', 'zoom'] as const;
const TRIM = ['start_ms', 'end_ms'] as const;
const CAST = /^(cast|action)_[1-9]$/;

/** Режим формы сервера (политика photo-n8n-v1 / video-n8n-v1) или null */
export function serverMode(kind: PhotoKind, form: StudioForm | null | undefined): ServerMode | null {
  if (!form) return null;
  return kind === 'photo' ? (photoPolicy(form)?.mode ?? null) : (videoPolicy(form)?.mode ?? null);
}
/** Режим сервера, которого ждёт вид шаблона; null — шаблон каталога (свой режим, без перехода) */
export function routeMode(kind: PhotoKind, v: Pick<PhotoView, 'mode' | 'src' | 'preset' | 'template' | 'none'>): ServerMode | null {
  if (v.template) return null;
  if (kind === 'photo') {
    if (v.preset === 'avatar') return 'avatar';
    if (v.preset === 'restore') return 'restore';
    if (v.preset === 'color') return 'colorize';
    if (v.mode === 'tx') return 'generate';
    return v.none ? 'edit' : 'style';
  }
  if (v.preset === 'cover') return 'product';
  if (v.preset === 'parallax') return 'parallax';
  if (v.mode === 'tx') return 'text';
  return v.none ? (v.src === 'vd' ? 'edit' : 'photo') : 'style';
}
/** Вход шаблона (setup) для режима сервера: пресет, режим сегмента и «Без стиля» (studio/engine.ts, строки «Фото» и «Видео») */
export function templateEntry(kind: PhotoKind, mode: ServerMode | null): { mode?: string; sty?: string; preset?: string } {
  if (kind === 'photo')
    switch (mode) {
      case 'generate':
        return { mode: 'tx' };
      case 'edit':
        return { mode: 'ph', sty: 'none' };
      case 'avatar':
        return { preset: 'avatar' };
      case 'restore':
        return { preset: 'restore' };
      case 'colorize':
        return { preset: 'color' };
      default:
        return { mode: 'ph' };
    }
  switch (mode) {
    case 'text':
      return { mode: 'tx' };
    case 'photo':
      return { mode: 'ph', sty: 'none' };
    case 'edit':
      return { mode: 'vd', sty: 'none' };
    case 'product':
      return { preset: 'cover' };
    case 'parallax':
      return { preset: 'parallax' };
    default:
      return { mode: 'fl' };
  }
}

const num = (value: number, digits = 4) => String(Math.round(value * 10 ** digits) / 10 ** digits);
const finite = (value: unknown) => {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : null;
};
/** Формат «Как у исходника» у сервера: auto у фото, src у видео */
export const sourceFormat = (kind: PhotoKind) => (kind === 'photo' ? 'auto' : 'src');

/** Входы формы по виду шаблона: prev — нынешние входы (ключи загрузок, свой референс и чужие ключи остаются) */
export function photoInputs(kind: PhotoKind, form: StudioForm, view: PhotoView, prev: Inputs, extras: PhotoExtras = {}): Inputs {
  const fields = studioFields(form);
  const field = (key: string) => fields.find(item => item.key === key);
  const next: Inputs = { ...prev };
  /** Опубликованное значение поля; не опубликовано — значение формы по умолчанию, иначе ключа нет */
  const put = (key: string, value: string | null) => {
    const f = field(key);
    if (!f || value === null || value === '') {
      delete next[key];
      return;
    }
    if (f.values.length && !f.values.includes(value)) {
      const fallback = f.default_value === undefined || f.default_value === null ? '' : String(f.default_value);
      if (fallback && f.values.includes(fallback)) next[key] = fallback;
      else delete next[key];
      return;
    }
    next[key] = value;
  };
  const mode = serverMode(kind, form);
  put('description', view.text.trim() || null);
  if (mode === 'product') {
    put('main_photo', '1');
    put('motion', view.none ? null : view.sty);
    delete next.style;
  } else put('style', view.refAct || view.none ? 'none' : view.sty);
  const format = view.ar ?? sourceFormat(kind);
  put('format', format);
  if (kind === 'photo') put('count', String(view.n));
  else put('duration', view.dur > 5 ? '10' : '5');
  const cuts = !!next.format && !['auto', 'src'].includes(next.format);
  const crop = view.src !== 'vd' && cuts && view.crop && CROP.every(key => field(key)) ? view.crop : null;
  if (crop) {
    next.crop_x = num(crop.cx);
    next.crop_y = num(crop.cy);
    next.zoom = num(crop.z, 3);
  } else CROP.forEach(key => delete next[key]);
  if (view.src === 'vd' && view.trim && TRIM.every(key => field(key))) {
    next.start_ms = String(Math.max(0, Math.round(view.trim.from * 1000)));
    next.end_ms = String(Math.round(view.trim.to * 1000));
  } else TRIM.forEach(key => delete next[key]);
  /* люди в кадре: по порядку в свободные слоты cast_N, действие — action_N; без людей (или не «По описанию») — слоты пустые */
  for (const key of Object.keys(next)) if (CAST.test(key)) delete next[key];
  (extras.cast ?? []).forEach((item, i) => {
    if (!field(`cast_${i + 1}`)) return;
    next[`cast_${i + 1}`] = item.ref;
    if (item.action && field(`action_${i + 1}`)) next[`action_${i + 1}`] = item.action;
  });
  if (extras.shoot === undefined) delete next.shoot;
  else next.shoot = extras.shoot;
  return next;
}

/** Вид шаблона из входов формы: только то, что во входах есть (остальное экран оставляет как было) */
export function photoView(kind: PhotoKind, form: StudioForm, inputs: Inputs): PhotoViewPatch {
  const fields = studioFields(form);
  const has = (key: string) => fields.some(item => item.key === key) && inputs[key] !== undefined && inputs[key] !== '';
  const out: PhotoViewPatch = {};
  const mode = serverMode(kind, form);
  if (has('description')) out.text = inputs.description;
  if (mode === 'product') {
    if (has('motion')) out.sty = inputs.motion;
  } else if (has('style')) out.sty = inputs.style;
  if (has('format')) out.ar = ['auto', 'src'].includes(inputs.format) ? 'src' : inputs.format;
  if (kind === 'photo' && has('count')) {
    const n = Number(inputs.count);
    if ([1, 2, 4].includes(n)) out.n = n;
  }
  if (kind === 'video' && has('duration')) {
    const d = Number(inputs.duration);
    if (d === 5 || d === 10) out.dur = d;
  }
  if (CROP.every(has)) {
    const [cx, cy, z] = CROP.map(key => finite(inputs[key]));
    if (cx !== null && cy !== null && z !== null) out.crop = { cx, cy, z };
  }
  if (TRIM.every(has)) {
    const [from, to] = TRIM.map(key => finite(inputs[key]));
    if (from !== null && to !== null && to > from) out.trim = { from: from / 1000, to: to / 1000 };
  }
  const cast: Array<{ ref: string; action: string }> = [];
  for (let i = 1; i <= 9; i++) {
    const ref = inputs[`cast_${i}`];
    if (ref && fields.some(item => item.key === `cast_${i}`)) cast.push({ ref, action: inputs[`action_${i}`] ?? '' });
  }
  if (cast.length) out.cast = cast;
  return out;
}

/** Ключи формы, которые вид пишет сам (поля формы шаблона, люди, «Съёмка»), — без ключей загрузок */
export function viewKeys(form: StudioForm): string[] {
  return studioFields(form)
    .map(item => item.key)
    .filter(key => !(UPLOAD_KEYS as readonly string[]).includes(key));
}
